import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import Tile from '../../common/Tile';
import useAsyncAction from '../../../commonComponents/Hooks';
import Input from '../../../commonComponents/appForm/Input';
import EmailField from '../../../commonComponents/appForm/EmailField';
import MobileNumberField from '../../../commonComponents/appForm/MobileNumberField';
import DropDownListField from '../../../commonComponents/appForm/DropDownListField';
import { DropDownListOption } from '../../../models/DropDownListOption';
import {
	PublicClientBasicInformation,
	createDefaultPublicClientBasicInformation
} from '../../../models/PublicClientModels';
import { auCulture, publicClientFamilyPageUrl, publicClientTypeSelectionPageUrl } from '../../../core/constants';
import {
	getPublicClientBasicInformation,
	createOrUpdatePublicClientBasicInformation
} from '../../../core/api/publicClientApi';
import {
	getOfficeLocationData,
	getDirectorsData,
	getDropdownOptions,
	getUnsortedDropdownOptions
} from '../../../lib/utils';
import Helmet from '../../../commonComponents/Helmet';
import PublicAutoLogoutLayout, { StyledFormDiv, SubTitleParagraph, TitleParagraph } from './common/PublicLayout';
import PublicForm from './common/PublicForm';
import PublicPageSteps from './common/PublicPageSteps';
import { genders, maritalStatuses, occupations, sourceOfReferral } from '../userProfile/formFieldsSourceData';
import DateField from '../../../commonComponents/appForm/DateField';
import { useProgress, useRequireProgress } from './common/PublicProgressContext';
import {
	ctxTermsAccepted,
	ctxClientTypeSelected,
	ctxClientBasicInfoCompleted,
	businessOwnerClientType
} from './common/publicConstants';
import FieldDecorator from '../../../commonComponents/fields/FieldDecorator';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import { useDirectors, useOffices } from '../../../core/api';
import { Office } from '../../../models/officeAndDirectors';
import config from '../../../config.general';
import { UserOfficeCountries } from '../../../core/enums';

const PublicBasicInformationPage = () => {
	const navigate = useNavigateWithAppName();
	const { clientTypeSelected, clientBasicInfoCompleted, updateProgress } = useProgress();

	const defaultValues = createDefaultPublicClientBasicInformation();
	const methods = useForm({ defaultValues });
	const {
		control,
		reset,
		setValue,
		getValues,
		setError,
		clearErrors,
		formState: { errors }
	} = methods;
	const officeId = useWatch({ control, name: 'officeId', defaultValue: null });
	const email = useWatch({ control: methods.control, name: 'email' });
	const confirmEmail = useWatch({ control: methods.control, name: 'confirmEmail' });

	const offices = useOffices() ?? [];
	const directors = useDirectors() ?? [];
	const [directorsData, setDirectorsData] = useState<DropDownListOption<number>[]>([]);
	const [currentStep] = useState<number>(1);
	const defaultOption: DropDownListOption<number> = { text: "I'm not sure which partner to choose", value: 0 };
	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();

	// Ensure the user has accepted the terms, selected a client type to access this page.
	useRequireProgress([ctxTermsAccepted, ctxClientTypeSelected]);

	useEffect(() => {
		if (clientBasicInfoCompleted) {
			performAsyncAction(async () => {
				const basicInfo = await getPublicClientBasicInformation(signal);
				reset(basicInfo);
			});
		}
	}, []);

	useEffect(() => {
		const newDirectorsData = getDirectorsData(directors, officeId);
		setDirectorsData([...newDirectorsData, defaultOption]);

		const directorId = getValues('directorId');
		if (!newDirectorsData.some((x) => x.value === directorId)) {
			setValue('directorId', null);
		}
	}, [officeId, directors]);

	useEffect(() => {
		clearErrors('confirmEmail');

		if (email !== '' && confirmEmail !== '' && email !== confirmEmail) {
			setError('confirmEmail', {
				type: 'manual',
				message: 'Email addresses do not match'
			});
		}
	}, [email, confirmEmail]);

	const handleSubmit = async (newBasicInfo: PublicClientBasicInformation) => {
		await performAsyncAction(async () => {
			const updatedBasicInfo = {
				...newBasicInfo,
				isBusinessOwner: clientTypeSelected === businessOwnerClientType
			};

			await createOrUpdatePublicClientBasicInformation(updatedBasicInfo, signal);
			updateProgress(ctxClientBasicInfoCompleted, true);

			navigate(publicClientFamilyPageUrl);
		});
	};

	const handleBackButtonClick = () => {
		navigate(publicClientTypeSelectionPageUrl);
	};

	return (
		<PublicAutoLogoutLayout>
			<Helmet>
				<title>Join Kelly+Partners | Prospective Client</title>
			</Helmet>
			<Tile>
				<PublicPageSteps currentStep={currentStep} />
				<TitleParagraph>Personal Details</TitleParagraph>
				<SubTitleParagraph>Let&apos;s start with your personal details.</SubTitleParagraph>
				<PublicForm
					methods={methods}
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
					primaryButtonText="Join"
					showPrimaryButtonEndIcon
					onBackButtonClick={handleBackButtonClick}
				>
					<StyledFormDiv>
						<Input label="Title" name="title" maxLength={50} />
						<Input label="First name" name="firstName" maxLength={50} required />
						<Input label="Last name" name="lastName" maxLength={50} required />
					</StyledFormDiv>
					<StyledFormDiv>
						<Input label="Preferred name" name="preferredName" maxLength={50} />
						<DropDownListField label="Gender" name="gender" data={getDropdownOptions(genders)} />
						<DropDownListField label="Marital status" name="maritalStatus" data={getDropdownOptions(maritalStatuses)} />
					</StyledFormDiv>
					<StyledFormDiv>
						<DropDownListField label="Occupation" name="occupation" data={getDropdownOptions(occupations)} />
						<DateField label="Date of birth" name="dateOfBirth" maxDate={new Date()} />
						<MobileNumberField label="Mobile number" name="mobilePhone" />
					</StyledFormDiv>
					<StyledFormDiv>
						<DropDownListField
							label="Choose your closest office"
							name="officeId"
							data={getOfficeLocationData(getOfficesPerCountry(offices))}
							required
						/>
						<DropDownListField label="Choose your Partner" name="directorId" data={directorsData} required />
						<DropDownListField
							label="How did you hear about us?"
							name="sourceOfReferral"
							data={getUnsortedDropdownOptions(sourceOfReferral)}
						/>
					</StyledFormDiv>

					<EmailField label="Email address" />
					<FieldDecorator errors={errors}>
						<EmailField name="confirmEmail" label="Confirm email address" />
					</FieldDecorator>
				</PublicForm>
			</Tile>
		</PublicAutoLogoutLayout>
	);
};

export default PublicBasicInformationPage;

const getOfficesPerCountry = (offices: Office[]) => {
	const officeCountry = config.currentCulture === auCulture ? UserOfficeCountries.AU : UserOfficeCountries.US;
	return offices.filter((x) => x.country === officeCountry);
};
